import { memo } from 'react';
import { format } from 'date-fns';
import { TFunction, useTranslation } from 'react-i18next';
import { Lead } from 'models/Leads';
import {
  getFullName,
  getLabelSource,
  getLeadCheckInDate,
  getLeadCheckOutDate,
  getLeadDetailLocalizedIntValue,
  getLeadLocalizedNightsCount,
} from 'utils/lead/leadUtils';
import useFormatCurrency from 'hooks/useFormatCurrency';
import { WarningIcon } from './LeadTooltip.styles';

function getGuestsDetails(
  { adultCount, childrenCount, infantCount, petCount }: Lead,
  t: TFunction,
) {
  return [
    getLeadDetailLocalizedIntValue(adultCount, 'adult', t),
    getLeadDetailLocalizedIntValue(childrenCount, 'child', t),
    getLeadDetailLocalizedIntValue(infantCount, 'infant', t),
    getLeadDetailLocalizedIntValue(petCount, 'pet', t),
  ]
    .filter((details) => !!details)
    .join(', ');
}

const LeadTooltipBookingDetails = ({
  lead,
  isOverlapping,
  isGroupBooking = false,
}: {
  lead: Lead;
  isOverlapping: boolean;
  isGroupBooking?: boolean;
}) => {
  const formatCurrency = useFormatCurrency();
  const { t } = useTranslation();

  const { source, order, ownerCommission } = lead;
  const fullName = getFullName(lead);
  const guestDetails = fullName ? getGuestsDetails(lead, t) : null;

  return (
    <>
      {isOverlapping && (
        <>
          <WarningIcon />
          <span style={{ color: 'red' }}>
            {t('componentLead.overlap.overlappingTooltip')}
          </span>
          <br />
        </>
      )}
      {isGroupBooking && (
        <>
          {t('pageCalendar.tooltip.groupBooking')}
          <br />
        </>
      )}
      {!!fullName && (
        <>
          {`${getLabelSource(source)}: ${getFullName(lead)}`}
          <br />
        </>
      )}
      {`${format(
        getLeadCheckInDate(lead),
        t('pageCalendar.tooltip.dateFormat'),
      )} - ${format(
        getLeadCheckOutDate(lead),
        t('pageCalendar.tooltip.dateFormat'),
      )} (${getLeadLocalizedNightsCount(lead, t)})`}
      {!!guestDetails && (
        <>
          <br />
          {guestDetails}
        </>
      )}
      {!!order && (
        <>
          <br />
          {formatCurrency({
            value: order.totalAmount,
            currency: order.currency,
            options: {
              round: false,
              removeDecimals:
                order.totalAmount % Math.floor(order.totalAmount) === 0,
            },
          })}
        </>
      )}
      {!!ownerCommission && (
        <>
          <br />
          {formatCurrency({
            value: ownerCommission.amount,
            currency: ownerCommission.currency,
            options: {
              round: true,
              removeDecimals: true,
            },
          })}
        </>
      )}
    </>
  );
};

LeadTooltipBookingDetails.defaultProps = {
  isGroupBooking: false,
};

export default memo(LeadTooltipBookingDetails);
